import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  Avatar,
  LinearProgress,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LanguageIcon from '@mui/icons-material/Language';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { designTokens, commonCardStylesDark } from '../../../theme/designTokens';
import { bg, border, overlay } from '../../../utils/darkModeAdapter';

/**
 * Props:
 *  - selectionData: {
 *      position: string,
 *      planData: {
 *        planName: string,
 *        planSummary: string,
 *        mediaList: Array<{
 *          name: string,
 *          cost: number,
 *          expectedApplicants: number,
 *          ...
 *        }>,
 *        ...
 *      },
 *      recommendationText: string,
 *      isDecided: boolean,
 *    }
 * 
 *  selectionData が null もしくは undefined の場合はフォールバック表示を行う。
 */

function MediaSelectionCard({ selectionData }) {
  const theme = useTheme();

  // フォールバック表示（データなし）
  if (!selectionData || !selectionData.planData) {
    return (
      <Card
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        sx={{
          mb: 3,
          backgroundColor: bg(theme).card,
          borderRadius: 3,
          border: `1px solid ${border(theme).light}`,
          boxShadow: designTokens.shadows.sm,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            <NewspaperIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            媒体選定が未確定です
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            現在、確定した媒体選定がありません。
            <br />
            媒体選定を行ってください。
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: overlay(theme).light,
              borderRadius: 2,
              border: `1px dashed ${overlay(theme).medium}`
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              媒体選定では、求職者が応募する広告媒体や求人サイトを選定し、
              各媒体からの想定応募者数や費用を計画します。
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // selectionData が存在する場合の表示
  const {
    position = '未設定職種',
    planData = {},
    recommendationText = '',
    isDecided = false,
  } = selectionData;

  const {
    planName = '',
    planSummary = '',
    mediaList = [],
    totalCost: providedTotalCost = 0,
    totalExpectedApplicants: providedTotalApplicants = 0,
  } = planData;

  // Calculate totals from media items if the provided totals are 0
  const calculatedTotalCost = mediaList.reduce((sum, media) => sum + (Number(media.cost) || 0), 0);
  const calculatedTotalApplicants = mediaList.reduce((sum, media) => sum + (Number(media.expectedApplicants) || 0), 0);
  
  // Use calculated values if provided values are 0
  const totalCost = providedTotalCost > 0 ? providedTotalCost : calculatedTotalCost;
  const totalExpectedApplicants = providedTotalApplicants > 0 ? providedTotalApplicants : calculatedTotalApplicants;

  // 決定済みかどうかでタイトルとデザインを変える
  const cardTitle = isDecided ? "確定した媒体選定計画" : "媒体選定計画案";
  const cardColor = isDecided ? theme.palette.primary.main : theme.palette.warning.main;
  const cardHeaderColor = isDecided ? theme.palette.primary.main : theme.palette.warning.main;

  // 費用を万円に変換する関数
  const toManYen = (cost) => {
    const costNum = Number(cost);
    if (isNaN(costNum)) return '0円';
    if (costNum === 0) return '0円';
    // 10,000円未満の場合はそのまま円で表示
    if (costNum < 10000) return `${costNum.toLocaleString()}円`;
    // 10,000円以上の場合は万円表示
    return `${Math.round(costNum / 10000).toLocaleString()}万円`;
  };

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        ...commonCardStylesDark(theme),
        mb: 3,
        backgroundColor: `${cardHeaderColor}0D`,
      }}
    >

      <CardContent
        sx={{
          py: 3,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              color: cardColor,
            }}
          >
            <NewspaperIcon sx={{ mr: 1, fontSize: '1.3rem' }} />
            {position}の{cardTitle}
          </Typography>
          
          {isDecided && (
            <Chip 
              icon={<CheckCircleIcon />} 
              label="確定済み" 
              color="primary"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>

        {/* プラン名・サマリー */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: overlay(theme).whiteLight,
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1, fontSize: '1.2rem', color: cardColor }} />
            {planName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {planSummary}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 媒体リスト */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'bold',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            color: cardColor,
          }}
        >
          <LanguageIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
          選定媒体
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          {mediaList.slice(0, 3).map((media, index) => (
            <Paper
              key={index}
              elevation={0}
              component={motion.div}
              initial={{ y: 5, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: overlay(theme).whiteLight,
                border: '1px solid rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {media.name}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '120px' }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: designTokens.colors.warning }} />
                    <Typography variant="body2" sx={{ color: designTokens.colors.warning, fontWeight: 500 }}>
                      {toManYen(media.cost)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleAltIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: designTokens.colors.primary }} />
                    <Typography variant="body2" sx={{ color: designTokens.colors.primary, fontWeight: 500 }}>
                      {parseInt(media.expectedApplicants || 0).toLocaleString()}人
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
          
          {mediaList.length > 3 && (
            <Box 
              sx={{ 
                p: 1.5, 
                textAlign: 'center',
                bgcolor: overlay(theme).light,
                borderRadius: 2,
                border: `1px dashed ${overlay(theme).medium}`
              }}
            >
              <Typography variant="body2" color="text.secondary">
                他 {mediaList.length - 3} 媒体があります
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 概要情報 */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: 'rgba(25, 118, 210, 0.05)',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: '0.9rem', mr: 1 }} />
                想定費用合計
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: designTokens.colors.primary }}>
                {toManYen(totalCost)}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: 'rgba(76, 175, 80, 0.05)',
                border: '1px solid rgba(76, 175, 80, 0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}
              >
                <PeopleAltIcon sx={{ fontSize: '0.9rem', mr: 1 }} />
                想定応募者数
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: designTokens.colors.success }}>
                {parseInt(totalExpectedApplicants || 0).toLocaleString()} 人
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default MediaSelectionCard; 